/**
 * useReadinessController — Owen's conversation choreography for the
 * Readiness experience.
 *
 * Durable state lives in the `readiness` Redux slice; this hook owns the
 * timing: typing delays (≈ 380ms + 11ms/char, capped +950ms), 240ms gaps
 * between messages, insight beats, section presentation, and the
 * ask-Owen-anything lane. All timers are cancellable (Back a question,
 * Start over, unmount).
 */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { readinessActions as A } from '../../store/readinessSlice';
import {
  visibleQuestions,
  getInsightFor,
  OWEN_INTRO_MESSAGES,
  OWEN_ANALYZING_MESSAGES,
  OWEN_SIDE_FOLLOWUP,
  OWEN_RESUME_CHIP_LABEL,
  OWEN_SKIP_RESPONSE,
  OWEN_RESTART_MESSAGE,
  owenAppIntroMessages,
  owenAppWrapupMessages,
  owenResumeSectionMessage,
} from '../../config/readinessQuestions';
import { buildAppSections } from '../../config/readinessAppSections';
import { matchPrograms, scoreBreakdown, scoreLabelFor, assessmentProgress } from '../../utils/readinessAssessment';
import { recommendProducts, profileFromReadinessAnswers } from '../../engine';
import {
  askOwenSideQuestion,
  createPasswordlessAccount,
  sendSignInCode,
  verifySignInCode,
  sendProgressReport,
  persistApplication,
} from '../../services/readinessService';

const PACE = 1; // conversation speed multiplier (1 = design spec)

export function useReadinessController() {
  const dispatch = useDispatch();
  const store = useStore();
  const timersRef = useRef([]);
  const chatElRef = useRef(null);
  const composerWrapRef = useRef(null);

  const getS = useCallback(() => store.getState().readiness, [store]);

  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms / PACE);
    timersRef.current.push(id);
    return id;
  }, []);

  const cancelTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  // ── Auto-scroll (instant + layout retries) ──────────────────
  // Use behavior:'auto' so timed retries aren't fighting CSS
  // scroll-behavior:smooth. Retries cover message paint and the
  // composer mounting (which shrinks the scroll viewport).
  const scrollDown = useCallback(() => {
    const go = () => {
      const el = chatElRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    };
    go();
    requestAnimationFrame(go);
    setTimeout(go, 60);
    setTimeout(go, 200);
    setTimeout(go, 400);
  }, []);

  // When the composer mounts or changes height, the flex scroll
  // area shrinks — re-scroll so the latest Owen question stays
  // fully visible above the input panel.
  useEffect(() => {
    const wrap = composerWrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return undefined;
    let prevHeight = wrap.getBoundingClientRect().height;
    const ro = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.height ?? 0;
      if (Math.abs(next - prevHeight) < 1) return;
      prevHeight = next;
      scrollDown();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [scrollDown]);

  // ── Owen speaks: typing indicator → message, sequentially ───
  // Items are strings, or { text, qid } for question messages (the qid
  // lets the thread offer the clickable "learn more" avatar).
  const say = useCallback((texts, cb) => {
    const list = texts.slice();
    const next = () => {
      if (!list.length) {
        if (cb) cb();
        return;
      }
      const item = list.shift();
      const text = typeof item === 'string' ? item : item.text;
      dispatch(A.setTyping(true));
      scrollDown();
      const delay = 380 + Math.min(950, text.length * 11);
      schedule(() => {
        dispatch(A.appendOwen(item));
        scrollDown();
        schedule(next, 240);
      }, delay);
    };
    next();
  }, [dispatch, schedule, scrollDown]);

  // ── Assessment flow ─────────────────────────────────────────
  const finish = useCallback(() => {
    say(OWEN_ANALYZING_MESSAGES, () => {
      dispatch(A.setTyping(true));
      scrollDown();
      schedule(() => {
        const best = matchPrograms(getS().answers)[0];
        dispatch(A.finishAssessment(best ? best.name : null));
      }, 1400);
    });
  }, [say, dispatch, schedule, scrollDown, getS]);

  const askQuestion = useCallback((qi) => {
    const s = getS();
    const vis = visibleQuestions(s.answers);
    if (qi >= vis.length) {
      finish();
      return;
    }
    const q = vis[qi];
    const isRefi = s.answers.purpose === 'refinance';
    const text = isRefi && q.textRefi ? q.textRefi : q.text;
    say([{ text, qid: q.id }], () => {
      dispatch(A.setQi(qi));
      // Composer appears after setQi and shrinks the scroll area —
      // scroll again once that layout settles.
      scrollDown();
    });
  }, [getS, say, dispatch, finish, scrollDown]);

  // ── Candidate-pool narrowing announcements ──────────────────
  // Owen tells the borrower how the product universe is shrinking,
  // but only when a meaningful threshold is crossed (not every answer).
  const narrowBucketRef = useRef(Infinity);
  const narrowingMessage = useCallback((remaining) => {
    const buckets = [150, 60, 25, 12, 5];
    const bucket = buckets.find((b) => remaining <= b);
    if (bucket === undefined || bucket >= narrowBucketRef.current) return null;
    narrowBucketRef.current = bucket;
    if (remaining <= 5) return `We're down to ${remaining} finalist loan option${remaining === 1 ? '' : 's'} — the next answers pick the winner.`;
    if (remaining <= 12) return `Nice — only ${remaining} strong candidates left. Now it's about which structure fits your goals.`;
    if (remaining <= 25) return `That narrowed things a lot — ${remaining} loan options still in the running.`;
    return `Just like that, we've gone from 350+ loan products to ${remaining} that could fit.`;
  }, []);

  /**
   * Ask the decision engine which question carries the most information
   * for the surviving product pool, then map it back to the readiness
   * question catalog. Falls back to first-unanswered order if the engine
   * picks something the UI can't currently show.
   */
  const pickNext = useCallback((answers) => {
    const vis = visibleQuestions(answers);
    let rec = null;
    try {
      rec = recommendProducts(profileFromReadinessAnswers(answers), {
        answeredKeys: new Set(Object.keys(answers)),
      });
    } catch {
      rec = null;
    }
    let nextIdx = -1;
    if (rec && rec.nextQuestion) {
      nextIdx = vis.findIndex((x) => x.id === rec.nextQuestion.key && answers[x.id] === undefined);
    }
    if (nextIdx === -1 && (!rec || rec.nextQuestion)) {
      // safety net: static order for anything the engine can't schedule
      nextIdx = vis.findIndex((x) => answers[x.id] === undefined);
    }
    return { nextIdx, remaining: rec ? rec.progress.remaining : null };
  }, []);

  const answer = useCallback((q, value, label) => {
    dispatch(A.recordAnswer({ qid: q.id, value, label }));
    scrollDown();
    const answers = getS().answers;
    const { nextIdx, remaining } = pickNext(answers);
    const insight = getInsightFor(q.id, value, answers)
      || (remaining != null ? narrowingMessage(remaining) : null);
    const proceed = () => {
      if (nextIdx === -1) {
        finish();
        return;
      }
      askQuestion(nextIdx);
    };
    if (insight) {
      dispatch(A.setTyping(true));
      scrollDown();
      schedule(() => {
        dispatch(A.appendInsight(insight));
        scrollDown();
        schedule(proceed, 500);
      }, 650);
    } else {
      schedule(proceed, 300);
    }
  }, [dispatch, getS, scrollDown, schedule, askQuestion, finish, pickNext, narrowingMessage]);

  const submitCurrency = useCallback(() => {
    const s = getS();
    const vis = visibleQuestions(s.answers);
    const q = s.qi >= 0 ? vis[s.qi] : null;
    if (!q || s.input === '') return;
    answer(q, s.input, '$' + s.input);
  }, [getS, answer]);

  const goBack = useCallback(() => {
    cancelTimers();
    dispatch(A.goBack());
  }, [cancelTimers, dispatch]);

  const restart = useCallback(() => {
    cancelTimers();
    narrowBucketRef.current = Infinity;
    dispatch(A.restart(OWEN_RESTART_MESSAGE));
  }, [cancelTimers, dispatch]);

  // ── Ask Owen anything ───────────────────────────────────────
  const askOwen = useCallback(async () => {
    const s = getS();
    const qText = s.ask.trim();
    if (!qText) return;
    dispatch(A.appendSideQ(qText));
    scrollDown();
    const { text } = await askOwenSideQuestion(qText, s.answers);
    dispatch(A.appendSideA(text));
    scrollDown();
    const after = getS();
    if (after.qi >= 0 || after.si >= 0) {
      say([OWEN_SIDE_FOLLOWUP], () => dispatch(A.setSidePending(true)));
    }
  }, [getS, dispatch, scrollDown, say]);

  const resume = useCallback(() => {
    dispatch(A.resumeFromSide(OWEN_RESUME_CHIP_LABEL));
    scrollDown();
    const s = getS();
    if (s.si >= 0) {
      const sec = buildAppSections(s.answers, s.selectedProgram)[s.si];
      if (sec) say([owenResumeSectionMessage(sec.title)]);
      return;
    }
    const vis = visibleQuestions(s.answers);
    const q = s.qi >= 0 ? vis[s.qi] : null;
    if (q) {
      const isRefi = s.answers.purpose === 'refinance';
      say([{ text: isRefi && q.textRefi ? q.textRefi : q.text, qid: q.id }]);
    }
  }, [dispatch, scrollDown, getS, say]);

  // ── Full application phase ──────────────────────────────────
  const finishApp = useCallback(() => {
    const s = getS();
    persistApplication(s.email, {
      answers: s.answers,
      sections: s.sections,
      selectedProgram: s.selectedProgram,
    });
    say(owenAppWrapupMessages(s.selectedProgram, s.email), () => dispatch(A.finishApp()));
  }, [getS, say, dispatch]);

  const presentSection = useCallback((i) => {
    const s = getS();
    const secs = buildAppSections(s.answers, s.selectedProgram);
    if (i >= secs.length) {
      finishApp();
      return;
    }
    say([secs[i].intro], () => {
      dispatch(A.presentSection(i));
      scrollDown();
    });
  }, [getS, say, dispatch, finishApp, scrollDown]);

  const beginApp = useCallback(() => {
    const s = getS();
    dispatch(A.beginApp());
    say(owenAppIntroMessages(s.selectedProgram, s.email), () => presentSection(0));
  }, [getS, dispatch, say, presentSection]);

  const continueApp = useCallback(() => {
    if (!getS().email.includes('@')) {
      dispatch(A.openGate());
      return;
    }
    beginApp();
  }, [getS, dispatch, beginApp]);

  /**
   * Re-enter the application at a specific section (from the answer
   * review screen) — picking up a skipped section or updating a saved
   * one. Saved values prefill via the presentSection reducer.
   */
  const resumeApp = useCallback((i) => {
    cancelTimers();
    const s = getS();
    const secs = buildAppSections(s.answers, s.selectedProgram);
    const idx = Math.max(0, Math.min(i, secs.length - 1));
    dispatch(A.beginApp());
    say([owenResumeSectionMessage(secs[idx].title)], () => {
      dispatch(A.presentSection(idx));
      scrollDown();
    });
  }, [cancelTimers, getS, dispatch, say, scrollDown]);

  const gateSubmit = useCallback(() => {
    const s = getS();
    if (!s.email.includes('@')) return;
    dispatch(A.closeGate());
    dispatch(A.setAccount(true));
    createPasswordlessAccount(s.email);
    beginApp();
  }, [getS, dispatch, beginApp]);

  /**
   * "Save before you leave" (logo click → homepage): create the passwordless
   * account and persist the conversation so far. Returns true when the caller
   * is clear to navigate away.
   */
  const leaveSave = useCallback(() => {
    const s = getS();
    if (!s.email.includes('@')) return false;
    dispatch(A.setAccount(true));
    createPasswordlessAccount(s.email);
    persistApplication(s.email, {
      answers: s.answers,
      sections: s.sections,
      selectedProgram: s.selectedProgram,
    });
    dispatch(A.closeLeave());
    return true;
  }, [getS, dispatch]);

  const saveSection = useCallback((skip) => {
    const s = getS();
    if (s.si < 0) return;
    const secs = buildAppSections(s.answers, s.selectedProgram);
    const sec = secs[s.si];
    const filled = Object.keys(s.form).filter((k) => s.form[k] !== '').length;
    const skipped = skip || filled === 0;
    const idx = s.si;
    dispatch(A.saveSection({ sectionId: sec.id, title: sec.title, skipped, filled }));
    scrollDown();
    if (skipped) {
      say([OWEN_SKIP_RESPONSE], () => presentSection(idx + 1));
    } else {
      schedule(() => presentSection(idx + 1), 350);
    }
  }, [getS, dispatch, scrollDown, say, schedule, presentSection]);

  // ── Sign in (passwordless, Supabase email OTP) ──────────────
  const sendCode = useCallback(async () => {
    const s = getS();
    if (!s.email.includes('@')) return;
    const { ok, error } = await sendSignInCode(s.email);
    if (ok) dispatch(A.setSigninStep(1));
    else dispatch(A.setSigninError(error || 'Could not send the code \u2014 please try again.'));
  }, [getS, dispatch]);

  const codeSubmit = useCallback(async () => {
    const s = getS();
    if (s.code.length !== 8) return;
    const { ok, error } = await verifySignInCode(s.email, s.code);
    if (ok) dispatch(A.signInSuccess());
    else dispatch(A.setSigninError(error || 'That code didn\u2019t match \u2014 try again or resend.'));
  }, [getS, dispatch]);

  // ── Email report ────────────────────────────────────────────
  const sendReport = useCallback(() => {
    const s = getS();
    if (!s.email.includes('@')) return;
    const { score } = scoreBreakdown(s.answers, { done: s.done || s.appMode || s.appDone, sections: s.sections });
    const { answered, total } = assessmentProgress(s.answers);
    sendProgressReport(s.email, {
      answered,
      total,
      score,
      label: scoreLabelFor(score),
      programs: s.done ? matchPrograms(s.answers).map((p) => p.name) : [],
    });
    dispatch(A.markReportSent());
  }, [getS, dispatch]);

  // ── Kickoff (StrictMode-safe: resumes a partially delivered intro) ──
  useEffect(() => {
    const s = getS();
    if (!s.started) {
      dispatch(A.markStarted());
      say(OWEN_INTRO_MESSAGES, () => askQuestion(0));
      return;
    }
    // Remounted mid-intro (React StrictMode dev double-mount): pick up
    // where the cancelled timers left off.
    const freshStart = s.qi === -1 && !s.done && !s.appMode
      && Object.keys(s.answers).length === 0
      && !s.msgs.some((m) => m.kind === 'user');
    if (freshStart) {
      const delivered = s.msgs.filter((m) => m.kind === 'owen').length;
      if (delivered < OWEN_INTRO_MESSAGES.length) {
        say(OWEN_INTRO_MESSAGES.slice(delivered), () => askQuestion(0));
      } else if (delivered === OWEN_INTRO_MESSAGES.length) {
        askQuestion(0);
      } else {
        dispatch(A.setQi(0));
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => cancelTimers(), [cancelTimers]);

  return {
    chatElRef,
    composerWrapRef,
    scrollDown,
    answer,
    submitCurrency,
    goBack,
    restart,
    askOwen,
    resume,
    continueApp,
    resumeApp,
    gateSubmit,
    leaveSave,
    saveSection,
    sendCode,
    codeSubmit,
    sendReport,
  };
}
